import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Separator } from '../components/ui/separator';
import {
  Layers, Users, ChevronDown, ChevronUp, Check, Sparkles,
  ArrowLeft, ShoppingCart, Send, Phone, Star, Clock,
  DollarSign, Tag, Info, Minus, Plus, ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_ICONS = {
  food: '🍽',
  drinks: '🍸',
  entertainment: '🎰',
  decor: '✨',
  staffing: '👔',
};

function ServiceToggleCard({ service, enabled, onToggle, guests, expanded, onExpand }) {
  const price = service.pricing_model === 'per_person'
    ? service.base_price * guests
    : service.base_price * (service.included_hours || 3);
  const priceLabel = service.pricing_model === 'per_person'
    ? `$${service.base_price}/person`
    : `$${service.base_price}/hr`;

  return (
    <Card
      className={`transition-all duration-300 border-2 ${enabled ? 'border-indigo-500/60 bg-indigo-500/5 shadow-lg shadow-indigo-500/10' : 'border-border/50 bg-card/50 opacity-70'}`}
      data-testid={`pkg-service-${service.service_id}`}
    >
      <CardContent className="p-0">
        <div className="flex items-start gap-3 p-4">
          <div className="flex items-center pt-1">
            <Switch
              checked={enabled}
              onCheckedChange={onToggle}
              data-testid={`toggle-${service.service_id}`}
            />
          </div>
          {service.image_url && (
            <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-lg overflow-hidden flex-shrink-0">
              <img src={service.image_url} alt={service.name} className="w-full h-full object-cover" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <div>
                <span className="text-lg mr-1.5">{CATEGORY_ICONS[service.category] || '📦'}</span>
                <h3 className={`inline text-sm sm:text-base font-bold ${enabled ? 'text-foreground' : 'text-muted-foreground'}`}>{service.name}</h3>
              </div>
              <div className="text-right flex-shrink-0">
                <p className={`text-sm font-bold ${enabled ? 'text-indigo-500' : 'text-muted-foreground'}`}>
                  ${price.toLocaleString()}
                </p>
                <p className="text-[10px] text-muted-foreground">{priceLabel}</p>
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{service.short_description}</p>
          </div>
        </div>

        {enabled && (
          <button
            onClick={onExpand}
            className="w-full flex items-center justify-center gap-1 text-xs text-indigo-400 py-2 border-t border-border/30 hover:bg-indigo-500/5 transition-colors"
            data-testid={`expand-${service.service_id}`}
          >
            {expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
            {expanded ? 'Hide Details' : "What's Included"}
          </button>
        )}

        {enabled && expanded && (
          <div className="px-4 pb-4 border-t border-border/20">
            {service.whats_included?.length > 0 && (
              <ul className="mt-3 space-y-1.5">
                {service.whats_included.map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-xs text-muted-foreground">
                    <Check className="h-3 w-3 text-emerald-400 mt-0.5 flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            )}
            {service.option_groups?.length > 0 && (
              <div className="mt-3 space-y-2">
                {service.option_groups.map(og => (
                  <div key={og.option_id} className="bg-muted/30 rounded-lg p-2.5">
                    <p className="text-xs font-semibold text-foreground mb-1">{og.name}</p>
                    <div className="flex flex-wrap gap-1">
                      {og.choices.map(c => (
                        <Badge key={c.choice_id} variant="outline" className="text-[10px] font-normal">
                          {c.name}{c.price_modifier > 0 && ` +$${c.price_modifier}`}
                        </Badge>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
            <Link
              to={`/denver/${service.category}/${service.sub_category || 'other'}/${service.slug}`}
              className="inline-flex items-center gap-1 text-xs text-indigo-400 hover:text-indigo-300 mt-3 font-medium"
            >
              View full service details <ChevronRight className="h-3 w-3" />
            </Link>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function PackageLandingPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [pkg, setPkg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [guests, setGuests] = useState(150);
  const [enabledServices, setEnabledServices] = useState({});
  const [expandedService, setExpandedService] = useState(null);
  const [showQuoteForm, setShowQuoteForm] = useState(false);
  const [quoteForm, setQuoteForm] = useState({ name: '', email: '', phone: '', date: '', notes: '' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetch(`${API}/api/packages/${slug}`)
      .then(r => r.json())
      .then(data => {
        setPkg(data);
        const enabled = {};
        (data.services || []).forEach(s => { enabled[s.service_id] = true; });
        setEnabledServices(enabled);
        setGuests(data.min_guests || 150);
      })
      .catch(() => toast.error('Package not found'))
      .finally(() => setLoading(false));
  }, [slug]);

  const toggleService = (sid) => {
    setEnabledServices(prev => ({ ...prev, [sid]: !prev[sid] }));
  };

  const totals = useMemo(() => {
    if (!pkg) return { subtotal: 0, discount: 0, total: 0, enabledCount: 0 };
    let subtotal = 0;
    let enabledCount = 0;
    (pkg.services || []).forEach(svc => {
      if (!enabledServices[svc.service_id]) return;
      enabledCount++;
      if (svc.pricing_model === 'per_person') {
        subtotal += svc.base_price * guests;
      } else {
        subtotal += svc.base_price * (svc.included_hours || 3);
      }
    });
    const discount = pkg.discount_type === 'percentage'
      ? subtotal * (pkg.discount_value / 100)
      : pkg.discount_type === 'flat' ? pkg.discount_value : 0;
    return { subtotal, discount, total: subtotal - discount, enabledCount };
  }, [pkg, guests, enabledServices]);

  const handleQuoteSubmit = async () => {
    if (!quoteForm.name || !quoteForm.email) {
      toast.error('Name and email are required');
      return;
    }
    setSubmitting(true);
    try {
      const selectedServices = (pkg.services || [])
        .filter(s => enabledServices[s.service_id])
        .map(s => ({
          service_id: s.service_id,
          name: s.name,
          base_price: s.base_price,
          pricing_model: s.pricing_model,
          included_hours: s.included_hours,
        }));

      const res = await fetch(`${API}/api/packages/${pkg.package_id}/quote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...quoteForm,
          guest_count: guests,
          selected_services: selectedServices,
          subtotal: totals.subtotal,
          discount: totals.discount,
          total: totals.total,
          package_name: pkg.name,
        }),
      });
      if (res.ok) {
        toast.success('Quote request sent! We\'ll be in touch within 24 hours.');
        setShowQuoteForm(false);
        setQuoteForm({ name: '', email: '', phone: '', date: '', notes: '' });
      } else {
        toast.error('Failed to submit. Please try again.');
      }
    } catch {
      toast.error('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center pt-16">
        <div className="animate-pulse text-muted-foreground">Loading package...</div>
      </div>
    );
  }

  if (!pkg) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center pt-16 gap-4">
        <p className="text-lg text-muted-foreground">Package not found</p>
        <Button onClick={() => navigate('/')} variant="outline">Go Home</Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pt-16" data-testid="package-landing">
      {/* Hero */}
      <div className="relative h-[340px] sm:h-[420px] overflow-hidden">
        <img
          src={pkg.image_url}
          alt={pkg.name}
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-black/20" />
        <div className="absolute inset-0 flex flex-col justify-end p-6 sm:p-12 max-w-5xl mx-auto">
          <div className="flex flex-wrap gap-2 mb-3">
            <Badge className="bg-indigo-600 text-white border-0"><Layers className="h-3 w-3 mr-1" /> Event Package</Badge>
            {pkg.discount_type !== 'none' && pkg.discount_value > 0 && (
              <Badge className="bg-emerald-500 text-white border-0"><Tag className="h-3 w-3 mr-1" /> Save {pkg.discount_value}% Bundle Discount</Badge>
            )}
            <Badge variant="outline" className="border-white/30 text-white/80"><Star className="h-3 w-3 mr-1" /> Featured</Badge>
          </div>
          <h1 className="text-3xl sm:text-5xl font-black text-white mb-2 leading-tight">{pkg.name}</h1>
          <p className="text-white/70 text-sm sm:text-base max-w-2xl leading-relaxed">{pkg.short_description}</p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">

          {/* Left: Services List */}
          <div className="lg:col-span-2 space-y-6">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <Sparkles className="h-5 w-5 text-indigo-400" />
                <h2 className="text-xl font-bold text-foreground">Included Services</h2>
              </div>
              <p className="text-sm text-muted-foreground mb-5">Toggle on/off to customize your package. Tap any service to see what's included.</p>
            </div>

            <div className="space-y-3">
              {(pkg.services || []).map(svc => (
                <ServiceToggleCard
                  key={svc.service_id}
                  service={svc}
                  enabled={!!enabledServices[svc.service_id]}
                  onToggle={() => toggleService(svc.service_id)}
                  guests={guests}
                  expanded={expandedService === svc.service_id}
                  onExpand={() => setExpandedService(expandedService === svc.service_id ? null : svc.service_id)}
                />
              ))}
            </div>

            {/* Description */}
            {pkg.description && (
              <div className="mt-8">
                <h3 className="text-lg font-bold text-foreground mb-3 flex items-center gap-2">
                  <Info className="h-5 w-5 text-muted-foreground" /> About This Package
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{pkg.description}</p>
              </div>
            )}
          </div>

          {/* Right: Sticky Pricing Sidebar */}
          <div className="lg:col-span-1">
            <div className="sticky top-20 space-y-4">
              {/* Guest Count */}
              <Card className="bg-card border-border" data-testid="guest-count-card">
                <CardContent className="p-5">
                  <Label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3 block">Guest Count</Label>
                  <div className="flex items-center gap-3">
                    <Button
                      size="icon"
                      variant="outline"
                      className="h-9 w-9"
                      onClick={() => setGuests(Math.max(pkg.min_guests, guests - 10))}
                      data-testid="decrease-guests"
                    >
                      <Minus className="h-4 w-4" />
                    </Button>
                    <Input
                      type="number"
                      value={guests}
                      onChange={(e) => setGuests(Math.max(pkg.min_guests, Math.min(pkg.max_guests, parseInt(e.target.value) || pkg.min_guests)))}
                      className="text-center text-lg font-bold h-9 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                      data-testid="guest-input"
                    />
                    <Button
                      size="icon"
                      variant="outline"
                      className="h-9 w-9"
                      onClick={() => setGuests(Math.min(pkg.max_guests, guests + 10))}
                      data-testid="increase-guests"
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                  <p className="text-[10px] text-muted-foreground text-center mt-2">{pkg.min_guests} – {pkg.max_guests} guests</p>
                </CardContent>
              </Card>

              {/* Pricing Summary */}
              <Card className="bg-gradient-to-br from-slate-900 to-slate-800 border-indigo-500/30" data-testid="pricing-summary">
                <CardContent className="p-5">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <DollarSign className="h-4 w-4 text-indigo-400" />
                      <span className="text-sm font-semibold text-white">Estimate</span>
                    </div>
                    <Badge variant="outline" className="text-indigo-300 border-indigo-500/30 text-[10px]">
                      {totals.enabledCount} of {(pkg.services || []).length} services
                    </Badge>
                  </div>

                  <div className="space-y-2 mb-4">
                    {(pkg.services || []).filter(s => enabledServices[s.service_id]).map(svc => {
                      const cost = svc.pricing_model === 'per_person'
                        ? svc.base_price * guests
                        : svc.base_price * (svc.included_hours || 3);
                      return (
                        <div key={svc.service_id} className="flex items-center justify-between text-xs">
                          <span className="text-slate-400 truncate mr-2">{svc.name.replace('Casino Royale: ', '').replace('Casino Royale:', '').trim()}</span>
                          <span className="text-slate-200 font-medium flex-shrink-0">${cost.toLocaleString()}</span>
                        </div>
                      );
                    })}
                  </div>

                  <Separator className="bg-white/10 my-3" />

                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className="text-slate-400">Subtotal</span>
                    <span className="text-slate-200">${totals.subtotal.toLocaleString()}</span>
                  </div>
                  {totals.discount > 0 && (
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span className="text-emerald-400">Bundle Discount ({pkg.discount_value}%)</span>
                      <span className="text-emerald-400">-${totals.discount.toLocaleString()}</span>
                    </div>
                  )}

                  <Separator className="bg-white/10 my-3" />

                  <div className="flex items-center justify-between">
                    <span className="text-sm font-bold text-white">Estimated Total</span>
                    <span className="text-2xl font-black text-indigo-400">${totals.total.toLocaleString()}</span>
                  </div>
                  <p className="text-[10px] text-slate-500 mt-1 text-right">
                    ${(totals.total / guests).toFixed(0)}/person for {guests} guests
                  </p>
                </CardContent>
              </Card>

              {/* CTA Buttons */}
              <div className="space-y-2">
                <Button
                  className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-semibold h-11"
                  onClick={() => setShowQuoteForm(true)}
                  data-testid="get-quote-btn"
                >
                  <Send className="h-4 w-4 mr-2" /> Get a Custom Quote
                </Button>
                <Button
                  variant="outline"
                  className="w-full h-10"
                  onClick={() => window.location.href = 'tel:7205550100'}
                  data-testid="call-btn"
                >
                  <Phone className="h-4 w-4 mr-2" /> Call (720) 555-0100
                </Button>
              </div>

              {/* Trust Signals */}
              <div className="flex items-center justify-center gap-4 text-[10px] text-muted-foreground">
                <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> 24hr Response</span>
                <span className="flex items-center gap-1"><Users className="h-3 w-3" /> 500+ Events</span>
                <span className="flex items-center gap-1"><Star className="h-3 w-3" /> 5-Star Rated</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Sticky Bottom Bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-card border-t border-border p-3 flex items-center justify-between z-20 lg:hidden" data-testid="mobile-pkg-bar">
        <div>
          <p className="text-xs text-muted-foreground">{totals.enabledCount} services / {guests} guests</p>
          <p className="text-lg font-black text-indigo-500">${totals.total.toLocaleString()}</p>
        </div>
        <Button
          className="bg-indigo-600 hover:bg-indigo-700 font-semibold"
          onClick={() => setShowQuoteForm(true)}
          data-testid="mobile-get-quote"
        >
          <Send className="h-4 w-4 mr-1" /> Get Quote
        </Button>
      </div>

      {/* Quote Form Dialog */}
      {showQuoteForm && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowQuoteForm(false)}>
          <div className="bg-card border border-border rounded-2xl max-w-md w-full p-6 shadow-2xl" onClick={e => e.stopPropagation()} data-testid="quote-form">
            <h3 className="text-xl font-bold text-foreground mb-1">Get a Custom Quote</h3>
            <p className="text-sm text-muted-foreground mb-5">
              {totals.enabledCount} services, {guests} guests — ${totals.total.toLocaleString()} estimated
            </p>
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Full Name *</Label>
                <Input value={quoteForm.name} onChange={e => setQuoteForm({...quoteForm, name: e.target.value})} placeholder="John Smith" data-testid="quote-name" />
              </div>
              <div>
                <Label className="text-xs">Email *</Label>
                <Input type="email" value={quoteForm.email} onChange={e => setQuoteForm({...quoteForm, email: e.target.value})} placeholder="john@example.com" data-testid="quote-email" />
              </div>
              <div>
                <Label className="text-xs">Phone</Label>
                <Input type="tel" value={quoteForm.phone} onChange={e => setQuoteForm({...quoteForm, phone: e.target.value})} placeholder="(555) 123-4567" data-testid="quote-phone" />
              </div>
              <div>
                <Label className="text-xs">Event Date</Label>
                <Input type="date" value={quoteForm.date} onChange={e => setQuoteForm({...quoteForm, date: e.target.value})} data-testid="quote-date" />
              </div>
              <div>
                <Label className="text-xs">Notes / Special Requests</Label>
                <textarea
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm min-h-[80px] resize-none focus:outline-none focus:ring-2 focus:ring-ring"
                  value={quoteForm.notes}
                  onChange={e => setQuoteForm({...quoteForm, notes: e.target.value})}
                  placeholder="Tell us about your event..."
                  data-testid="quote-notes"
                />
              </div>
            </div>
            <div className="flex gap-2 mt-5">
              <Button variant="outline" className="flex-1" onClick={() => setShowQuoteForm(false)}>Cancel</Button>
              <Button className="flex-1 bg-indigo-600 hover:bg-indigo-700" onClick={handleQuoteSubmit} disabled={submitting} data-testid="submit-quote">
                {submitting ? 'Sending...' : 'Send Quote Request'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
